import { useTranslation } from 'react-i18next';
import { Pagination } from '@/components';
import { Row } from '@/components/SettingsLayout';
import { formatCurrency } from '@/utils/currency';
import { useNavigate, useLocation } from 'react-router-dom';
import { residualsDateFormatter } from '@/utils/helpers/dateFormater';
import styles from './styles.module.css';
import { EmptyResiduals } from './components/EmptyResiduals';
import { Text, Icon, IconType } from '@/ui';
import classNames from 'classnames';
import { useGetFeesReportHistoryQuery } from '@/store/slices/pricingSlice';
import { useState, useCallback, useMemo } from 'react';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';
import { useGetUser } from '@/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import ImageWithFallback from '@/ui/ImageWithFallBack/ImageWithFallBack';
import accruingStatusIcon from '@/assets/icons/accruingStatus.svg';
import dueStatusIcon from '@/assets/icons/dueStatus.svg';
import paidStatusIcon from '@/assets/icons/paidStatus.svg';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table';
import type { CardField } from '@/components/data-table';

type HistoryFilter = {
  limit: number;
  offset: number;
};

type HistoryReport = {
  _id: string;
  feesReportId?: string;
  startDate?: string | Date;
  endDate?: string | Date;
  status?: string;
  datePaid?: string | Date;
  residualsEarned?: number;
};

function SortableHeader({
  label,
  field,
  sortColumn,
  sortValue,
  onSort,
}: {
  label: string;
  field: string;
  sortColumn: string | null;
  sortValue: 'asc' | 'desc' | null;
  onSort: (field: string) => void;
}) {
  const iconClass = classNames(styles.sortIcon, {
    [styles.filterNone]: sortColumn !== field,
    [styles.filterAsc]: sortColumn === field && sortValue === 'asc',
    [styles.filterDesc]: sortColumn === field && sortValue === 'desc',
  });
  return (
    <Row gapSize='none' align='center'>
      <Text size='s' weight='semi-bold'>
        {label}
      </Text>
      <button
        type='button'
        className={iconClass}
        onClick={(e) => {
          e.stopPropagation();
          onSort(field);
        }}
        style={{
          cursor: 'pointer',
          marginLeft: '8px',
          display: 'flex',
          alignItems: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
        }}
        aria-label={`Sort by ${label}`}
      >
        <Icon width={16} height={16} fill='transparent' type={IconType.ArrowsSplit} />
      </button>
    </Row>
  );
}

const getStatusIcon = (status?: string) => {
  switch (status?.toLowerCase()) {
    case 'paid':
      return paidStatusIcon;
    case 'due':
      return dueStatusIcon;
    case 'accruing':
    default:
      return accruingStatusIcon;
  }
};

function ResidualHistory() {
  const { company } = useGetUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortValue, setSortValue] = useState<'asc' | 'desc' | null>(null);

  const { filter, page, setPage, perPage, setPerPage } = usePaginationFilter<HistoryFilter>({
    limit: 10,
    offset: 0,
  });

  const { data: reportHistoryData, isError: isHistoryError } = useGetFeesReportHistoryQuery(
    company?._id
      ? {
          companyId: company._id,
          query: {
            limit: filter.limit,
            offset: filter.offset,
            sortColumn: sortColumn || undefined,
            sortValue: sortValue || undefined,
          },
        }
      : skipToken,
  );

  const historyData: HistoryReport[] = reportHistoryData?.data ?? [];
  const totalCount = reportHistoryData?.totalCount ?? 0;

  const handleSetPerPage = useCallback(
    (value: number | string) => {
      setPerPage(Number(value));
      setPage(1);
    },
    [setPerPage, setPage],
  );

  const handleSort = useCallback(
    (field: string) => {
      let newSortColumn: string | null = field;
      let newSortValue: 'asc' | 'desc' | null = 'asc';

      if (sortColumn === field) {
        if (sortValue === 'asc') {
          newSortValue = 'desc';
        } else if (sortValue === 'desc') {
          newSortColumn = null;
          newSortValue = null;
        }
      }

      setPage(1);
      setSortColumn(newSortColumn);
      setSortValue(newSortValue);
    },
    [sortColumn, sortValue, setPage],
  );

  const columnHelper = createColumnHelper<HistoryReport>();
  const mobileCardFields = useMemo<CardField<HistoryReport>[]>(
    () => [
      {
        key: 'period',
        primary: true,
        render: (row) =>
          `${residualsDateFormatter(row.original.startDate ?? '')} - ${residualsDateFormatter(row.original.endDate ?? '')}`,
      },
      {
        key: 'status',
        label: t('Residuals.status'),
        render: (row) => {
          const s = (row.original.status ?? '').toLowerCase();
          if (s === 'paid') return t('Residuals.status_paid') ?? 'Paid';
          if (s === 'due') return t('Residuals.status_due') ?? 'Due';
          return t('Residuals.status_accruing') ?? 'Accruing';
        },
      },
      {
        key: 'datePaid',
        label: t('Residuals.paid_date'),
        render: (row) => residualsDateFormatter(row.original.datePaid ?? ''),
      },
      {
        key: 'residualsEarned',
        label: t('Residuals.residuals'),
        render: (row) => formatCurrency(row.original.residualsEarned ?? 0, 'USD'),
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (r) =>
          `${residualsDateFormatter(r.startDate ?? '')} - ${residualsDateFormatter(r.endDate ?? '')}`,
        {
          id: 'period',
          header: () => (
            <Row gapSize='none'>
              <Text size='s' weight='semi-bold'>
                {t('Residuals.payment_period')}
              </Text>
            </Row>
          ),
          cell: ({ row }) => (
            <Text size='s' weight='light-medium'>
              {`${residualsDateFormatter(row.original.startDate ?? '')} - ${residualsDateFormatter(row.original.endDate ?? '')}`}
            </Text>
          ),
          size: 220,
        },
      ),
      columnHelper.accessor((r) => r.status, {
        id: 'status',
        header: () => (
          <Row gapSize='none'>
            <Text size='s' weight='semi-bold'>
              {t('Residuals.status')}
            </Text>
          </Row>
        ),
        cell: ({ row }) => (
          <ImageWithFallback
            src={getStatusIcon(row.original.status)}
            placeholderSrc={accruingStatusIcon}
            width={40}
            height={48}
          />
        ),
        size: 100,
      }),
      columnHelper.accessor((r) => r.datePaid, {
        id: 'datePaid',
        header: () => (
          <SortableHeader
            label={t('Residuals.paid_date')}
            field='datePaid'
            sortColumn={sortColumn}
            sortValue={sortValue}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <Text size='s' weight='light-medium'>
            {residualsDateFormatter(row.original.datePaid ?? '')}
          </Text>
        ),
        size: 140,
      }),
      columnHelper.accessor((r) => r.residualsEarned ?? 0, {
        id: 'residualsEarned',
        header: () => (
          <SortableHeader
            label={t('Residuals.residuals')}
            field='residualsEarned'
            sortColumn={sortColumn}
            sortValue={sortValue}
            onSort={handleSort}
          />
        ),
        cell: ({ row }) => (
          <Text size='s' weight='light-medium'>
            {formatCurrency(row.original.residualsEarned ?? 0, 'USD')}
          </Text>
        ),
        size: 120,
      }),
    ],
    [t, sortColumn, sortValue, handleSort],
  );

  const table = useReactTable({
    data: historyData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isHistoryError) {
    return (
      <div>
        <Text size='s' weight='light-medium'>
          {t('Residuals.error_loading') || 'Error loading residuals'}
        </Text>
      </div>
    );
  }

  if (historyData.length === 0) {
    return <EmptyResiduals />;
  }

  return (
    <>
      <Text size='m' weight='semi-bold'>
        {t('Residuals.previous')}
      </Text>
      <DataTable
        table={table}
        mobileCardFields={mobileCardFields}
        onRowClick={(row) => {
          const report = row.original;
          const reportId = report.feesReportId || report._id;
          navigate(`${pathname}/${reportId}`, { state: { report } });
        }}
      />
      {totalCount >= 10 && (
        <Pagination
          page={page}
          totalPages={Math.ceil(totalCount / Number(perPage))}
          setPage={setPage}
          perPage={String(perPage)}
          setPerPage={handleSetPerPage}
          maxResults={totalCount}
        />
      )}
    </>
  );
}

export default ResidualHistory;
